import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { updateQuery } from 'core/query-client/query-client';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { ErrorMapping } from 'core/types/error-mapping';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { useState } from 'react';
import { OperatorCustomNotificationWriteApi } from 'modules/settings/pods/settings-operator-custom-notifications/service/operator-custom-notifications.api';
import {
    createOperatorCustomNotification,
    updateOperatorCustomNotification,
} from 'modules/settings/pods/settings-operator-custom-notifications/service/operator-custom-notifications.service';
import { getCustomNotificationNameValidationCode } from 'modules/settings/components/custom-notifications/custom-notification-name-error.business';
import { OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.constants';
import { OperatorCustomNotificationFormMode } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.model';

interface UseOperatorCustomNotificationSaverProps {
    onSuccess: VoidFunction;
}

export interface OperatorCustomNotificationUpdate {
    payload: OperatorCustomNotificationWriteApi;
    notificationId: string;
}

const feedbackKey = (mode: OperatorCustomNotificationFormMode, outcome: 'success' | 'error') =>
    `${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.${mode}.notifications.${outcome}`;

export const useOperatorCustomNotificationSaver = ({ onSuccess }: UseOperatorCustomNotificationSaverProps) => {
    const { t } = useTranslation();
    const [nameCustomValidationCode, setNameCustomValidationCode] = useState<ErrorMapping['type'] | undefined>();

    const resetNameCustomValidationCode = () => setNameCustomValidationCode(undefined);

    const notifyWritten = (mode: OperatorCustomNotificationFormMode) => {
        void updateQuery(settingsKeys.operatorCustomNotifications());
        showSuccessNotification(t(feedbackKey(mode, 'success')));
        onSuccess();
    };

    const notifyFailure = (mode: OperatorCustomNotificationFormMode) => (error: unknown) => {
        setNameCustomValidationCode(getCustomNotificationNameValidationCode(error as Error));
        showErrorNotification(t(feedbackKey(mode, 'error')));
    };

    const { mutate: addNotification, isPending: isAdding } = usePocMutation({
        mutationFn: (payload: OperatorCustomNotificationWriteApi) => createOperatorCustomNotification(payload),
        onSuccess: () => notifyWritten('add'),
        onError: notifyFailure('add'),
    });

    const {
        mutate: editNotification,
        mutateAsync: editNotificationAsync,
        isPending: isEditing,
    } = usePocMutation({
        mutationFn: ({ payload, notificationId }: OperatorCustomNotificationUpdate) =>
            updateOperatorCustomNotification(payload, notificationId),
        onSuccess: () => notifyWritten('edit'),
        onError: notifyFailure('edit'),
    });

    return {
        isSubmitting: isAdding || isEditing,
        nameCustomValidationCode,
        resetNameCustomValidationCode,
        addNotification,
        editNotification,
        editNotificationAsync,
    };
};
